import { useGoogleCalendarEventsList } from "@/components/EntitySelector/integrations/GoogleCalendarSelector";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useIntegrationConnection } from "@/hooks/useIntegrationConnection";
import { GoogleCalendarCalendarEventEntity } from "@cohesive/api-entities";
import { getEntityId, Spinner } from "@cohesive/ui-components";
import { ControlProps, optionIs, rankWith } from "@jsonforms/core";
import { withJsonFormsControlProps } from "@jsonforms/react";
import { Calendar, ChevronsUpDown, X } from "lucide-react";
import React, { useMemo, useState } from "react";

interface GoogleCalendarEventControlProps extends ControlProps {
  data: GoogleCalendarCalendarEventEntity | string | undefined;
}

const GoogleCalendarEventControlComponent: React.FC<
  GoogleCalendarEventControlProps
> = ({ data, enabled, handleChange, path, label, visible }) => {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const integration = useIntegrationConnection("google_calendar");

  // Extract ID from data (handles both string ID and entity object)
  // Handle both string IDs and entity objects
  const selectedId =
    typeof data === "string" ? data : data ? getEntityId(data as any) : null;

  // Calculate date range for upcoming events (next 30 days) - useMemo to prevent recreation
  const dateRange = React.useMemo(() => {
    const today = new Date();
    const thirtyDaysFromNow = new Date(today);
    thirtyDaysFromNow.setDate(today.getDate() + 30);
    return {
      time_min: today.toISOString(),
      time_max: thirtyDaysFromNow.toISOString(),
    };
  }, []);

  const {
    data: eventsResponse,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useGoogleCalendarEventsList(integration, {
    calendar_id: "primary",
    time_min: dateRange.time_min,
    time_max: dateRange.time_max,
    max_results: 50,
    single_events: true,
    order_by: "startTime",
  });

  // Flatten pages into single array - the API returns data in page.data, not page.entities
  const allEvents =
    eventsResponse?.pages?.flatMap((page) => page.data || []) ?? [];

  // Filter events based on search query
  const filteredEvents = allEvents.filter(
    (event: GoogleCalendarCalendarEventEntity) => {
      const title = event.data.title || event.data.summary || "";
      const description = event.data.description || "";
      const query = searchQuery.toLowerCase();
      return (
        title.toLowerCase().includes(query) ||
        description.toLowerCase().includes(query)
      );
    },
  );

  const selectedEvent = useMemo(
    () => allEvents.find((event) => selectedId === event.data.id),
    [allEvents, selectedId],
  );

  const renderEventIcon = () => {
    return (
      <div className="flex items-center justify-center w-7 h-7 shrink-0 rounded-full bg-muted">
        <Calendar className="w-4! h-4!" />
      </div>
    );
  };

  if (!visible) return null;

  return (
    <div className="w-full">
      {label && <label className="text-sm font-medium">{label}</label>}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between"
            disabled={!enabled || !integration}
          >
            {selectedEvent ? (
              <div className="flex items-center gap-2 overflow-hidden">
                {renderEventIcon()}
                <span className="truncate">
                  {selectedEvent.data.title || selectedEvent.data.summary}
                </span>
              </div>
            ) : (
              <span className="text-muted-foreground">Select event...</span>
            )}
            <div className="flex items-center gap-1 ml-2 shrink-0">
              {selectedEvent && (
                <div
                  role="button"
                  aria-label="Clear selection"
                  className="cursor-pointer"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleChange(path, null);
                  }}
                >
                  <X className="h-4 w-4 opacity-50 hover:opacity-100" />
                </div>
              )}
              <ChevronsUpDown className="h-4 w-4 opacity-50" />
            </div>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[400px] p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Search events..."
              value={searchQuery}
              onValueChange={setSearchQuery}
            />
            <CommandList
              onScroll={(e: React.UIEvent<HTMLDivElement>) => {
                const element = e.currentTarget;
                const bottom =
                  element.scrollHeight - element.scrollTop ===
                  element.clientHeight;

                if (bottom && hasNextPage && !isFetchingNextPage) {
                  fetchNextPage();
                }
              }}
            >
              {isLoading ? (
                <div className="flex items-center justify-center p-4">
                  <Spinner size="sm" />
                </div>
              ) : (
                <>
                  <CommandEmpty>
                    No events found in the next 30 days.
                  </CommandEmpty>
                  <CommandGroup heading="Upcoming Events">
                    {filteredEvents.map(
                      (event: GoogleCalendarCalendarEventEntity) => (
                        <CommandItem
                          key={event.data.id}
                          value={event.data.id}
                          onSelect={() => {
                            // Return full entity object
                            handleChange(path, event);
                            setOpen(false);
                          }}
                        >
                          {renderEventIcon()}
                          <span className="truncate">
                            {event.data.title ||
                              event.data.summary ||
                              "Untitled Event"}
                          </span>
                        </CommandItem>
                      ),
                    )}
                    {isFetchingNextPage && (
                      <div className="py-2 text-center">
                        <Spinner size="sm" />
                      </div>
                    )}
                  </CommandGroup>
                </>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export const GoogleCalendarEventControlTester = rankWith(
  10,
  optionIs("control", "google_calendar_event"),
);

export const GoogleCalendarEventControl = withJsonFormsControlProps(
  GoogleCalendarEventControlComponent,
);
